/**
 * Hook for managing material cascade updates in forms
 * Implements Excel's automatic VLOOKUP behavior when materials change
 *
 * Refactored to use MaterialCascadeService for better performance and testability
 */
import { useCallback } from 'react';
import { materialCascadeService } from '../services/MaterialCascadeService';
import { calculateRailwayGeogridCascadeEffects, getMinimumSuggestedThicknessForBase, getMinimumSuggestedThicknessForSubbase } from '../services/ProjectTypeService';
import { getBallastCbr, getBallastOptions } from '../services/BallastCbrDatabase';
import { logDebug, logError, LogTag } from '../utils/utils';

interface FormValues {
    baseMaterial?: string;
    subbaseMaterial?: string;
    surfaceMaterial?: string;
    baseGeogrid?: string;
    subbaseGeogrid?: string;

    // Fields that update automatically (Excel VLOOKUP targets)
    layerCoefficienta1?: number;
    layerCoefficienta2?: number;
    layerCoefficienta3?: number;
    effectivePorosity?: number;
    permeabilityCoefficient?: number;
    cbrBaseCourse?: number;
    cbrSubbaseCourse?: number;
    ev2BaseCourse?: number;
    ev2SubbaseCourse?: number;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
}

/**
 * Universal form cascade hook for dynamic form management
 * Handles automatic field relationships and cascading updates across all field types
 * Supports materials, geogrids, railway fields, and any future cascade requirements
 * Replicates Excel's automatic VLOOKUP updates when form selections change
 */
export const useFormCascade = <T extends FormValues>() => {
    /**
     * Handle base material change and update all dependent fields
     * Excel Logic: G11 change → G12,G18,G19,G21,G22 update
     *
     * Refactored to use MaterialCascadeService for consistent behavior and caching
     */
    const handleBaseMaterialChange = useCallback((baseMaterialId: string, setFieldValue: (field: string, value: any) => void, values: T) => {
        logDebug('useFormCascade', `Base material changed to: ${baseMaterialId}`, undefined, LogTag.FORM_CASCADE);

        // Early return if required parameters not provided
        if (!setFieldValue || !values) {
            logDebug('useFormCascade', 'setFieldValue or values not provided, skipping base material change', undefined, LogTag.FORM_CASCADE);
            return;
        }

        try {
            // Get auto-selected geogrid using service
            const autoSelectedGeogrid = materialCascadeService.getAutoSelectedGeogrid('base', baseMaterialId);
            logDebug('useFormCascade', `Auto-selected geogrid: ${autoSelectedGeogrid}`, undefined, LogTag.FORM_CASCADE);

            // Calculate all field updates using service
            const fieldUpdates = materialCascadeService.calculateMaterialCascadeUpdate('base', baseMaterialId, autoSelectedGeogrid === 'None' ? null : autoSelectedGeogrid);

            logDebug('useFormCascade', 'Updating fields with service results:', fieldUpdates, LogTag.FORM_CASCADE);

            // Apply all field updates using setFieldValue
            Object.entries(fieldUpdates).forEach(([fieldPath, value]) => {
                setFieldValue(fieldPath, value);
            });

            // Update minimum thickness suggested based on current ESAL value
            const esalValue = (values as any)?.trafficLoad?.esalFinalValue; // eslint-disable-line @typescript-eslint/no-explicit-any
            if (esalValue && typeof esalValue === 'number' && esalValue > 0) {
                const baseMinThickness = getMinimumSuggestedThicknessForBase(esalValue);
                setFieldValue('baseCourse.minThicknessSuggested', baseMinThickness);
                logDebug('useFormCascade', `Updated base minimum thickness to ${baseMinThickness} based on ESAL ${esalValue}`, undefined, LogTag.FORM_CASCADE);
            } else {
                logDebug('useFormCascade', 'ESAL value not available, skipping minimum thickness update', { esalValue }, LogTag.FORM_CASCADE);
            }

            // Validate geogrid compatibility and clear if invalid
            const geogridEnabled = materialCascadeService.validateGeogridCompatibility('base', baseMaterialId);
            if (!geogridEnabled && values && values.baseGeogrid) {
                setFieldValue('baseCourse.baseGeogrid', ''); // Clear invalid geogrid selection
            }
        } catch (error) {
            logError('useFormCascade', 'Error in base material change:', error, LogTag.FORM_CASCADE);
        }
    }, []);

    /**
     * Handle subbase material change and update all dependent fields
     * Refactored to use MaterialCascadeService
     */
    const handleSubbaseMaterialChange = useCallback((subbaseMaterialId: string, setFieldValue: (field: string, value: any) => void, values: T) => {
        logDebug('useFormCascade', `Subbase material changed to: ${subbaseMaterialId}`, undefined, LogTag.FORM_CASCADE);

        // Early return if required parameters not provided
        if (!setFieldValue || !values) {
            logDebug('useFormCascade', 'setFieldValue or values not provided, skipping subbase material change', undefined, LogTag.FORM_CASCADE);
            return;
        }

        try {
            // Get auto-selected geogrid using service
            const autoSelectedGeogrid = materialCascadeService.getAutoSelectedGeogrid('subbase', subbaseMaterialId);
            logDebug('useFormCascade', `Auto-selected subbase geogrid: ${autoSelectedGeogrid}`, undefined, LogTag.FORM_CASCADE);

            // Calculate all field updates using service
            const fieldUpdates = materialCascadeService.calculateMaterialCascadeUpdate('subbase', subbaseMaterialId, autoSelectedGeogrid === 'None' ? null : autoSelectedGeogrid);

            logDebug('useFormCascade', 'Updating subbase fields with service results:', fieldUpdates, LogTag.FORM_CASCADE);

            // Apply all field updates using setFieldValue
            Object.entries(fieldUpdates).forEach(([fieldPath, value]) => {
                setFieldValue(fieldPath, value);
            });

            // Update minimum thickness suggested based on current ESAL value
            const esalValue = (values as any)?.trafficLoad?.esalFinalValue; // eslint-disable-line @typescript-eslint/no-explicit-any
            if (esalValue && typeof esalValue === 'number' && esalValue > 0) {
                const subbaseMinThickness = getMinimumSuggestedThicknessForSubbase(esalValue);
                setFieldValue('subbaseCourse.minThicknessSuggested', subbaseMinThickness);
                logDebug('useFormCascade', `Updated subbase minimum thickness to ${subbaseMinThickness} based on ESAL ${esalValue}`, undefined, LogTag.FORM_CASCADE);
            } else {
                logDebug('useFormCascade', 'ESAL value not available, skipping subbase minimum thickness update', { esalValue }, LogTag.FORM_CASCADE);
            }

            // Validate geogrid compatibility and clear if invalid
            const geogridEnabled = materialCascadeService.validateGeogridCompatibility('subbase', subbaseMaterialId);
            if (!geogridEnabled && values && values.subbaseGeogrid) {
                setFieldValue('subbaseCourse.subbaseGeogrid', '');
            }
        } catch (error) {
            logError('useFormCascade', 'Error in subbase material change:', error, LogTag.FORM_CASCADE);
        }
    }, []);

    /**
     * Handle surface material change and update all dependent fields
     */
    /**
     * Handle surface material change and update all dependent fields
     * Refactored to use MaterialCascadeService
     */
    const handleSurfaceMaterialChange = useCallback((surfaceMaterialId: string, setFieldValue: (field: string, value: any) => void, values: T) => {
        logDebug('useFormCascade', `Surface material changed to: ${surfaceMaterialId}`, undefined, LogTag.FORM_CASCADE);

        // Early return if required parameters not provided
        if (!setFieldValue) {
            logDebug('useFormCascade', 'setFieldValue not provided, skipping surface material change', undefined, LogTag.FORM_CASCADE);
            return;
        }

        try {
            // Calculate all field updates using service
            const fieldUpdates = materialCascadeService.calculateMaterialCascadeUpdate('surface', surfaceMaterialId);

            logDebug('useFormCascade', 'Updating surface fields with service results:', fieldUpdates, LogTag.FORM_CASCADE);

            // Apply all field updates using setFieldValue
            Object.entries(fieldUpdates).forEach(([fieldPath, value]) => {
                setFieldValue(fieldPath, value);
            });
        } catch (error) {
            logError('useFormCascade', 'Error in surface material change:', error, LogTag.FORM_CASCADE);
        }
    }, []);

    /**
     * Handle geogrid change and update LCR-dependent fields
     * Excel Logic: Geogrid change → G85 (LCR) → G87 (stabilized coefficient)
     * Refactored to use MaterialCascadeService for validation and caching
     */
    const handleBaseGeogridChange = useCallback((geogridId: string, setFieldValue: (field: string, value: any) => void, values: T) => {
        logDebug('useFormCascade', `Base geogrid changed to: ${geogridId}`, undefined, LogTag.FORM_CASCADE);

        // Early return if required parameters not provided
        if (!setFieldValue || !values) {
            logDebug('useFormCascade', 'setFieldValue or values not provided, skipping base geogrid change', undefined, LogTag.FORM_CASCADE);
            return;
        }

        if (!values.baseMaterial) {
            logDebug('useFormCascade', 'No base material selected, cannot update geogrid', undefined, LogTag.FORM_CASCADE);
            return;
        }

        try {
            // Validate geogrid compatibility using service
            const isValid = materialCascadeService.validateGeogridCompatibility('base', values.baseMaterial, geogridId);
            if (!isValid && geogridId) {
                logDebug('useFormCascade', `Invalid geogrid selection for material: ${values.baseMaterial}`, undefined, LogTag.FORM_CASCADE);
                return;
            }

            // Update base geogrid field
            setFieldValue('baseCourse.baseGeogrid', geogridId);

            // Recalculate cascade effects with new geogrid using service for consistency
            const fieldUpdates = materialCascadeService.calculateMaterialCascadeUpdate('base', values.baseMaterial, geogridId);

            // Apply relevant field updates (excluding material ID since it's not changing)
            Object.entries(fieldUpdates).forEach(([fieldPath, value]) => {
                if (!fieldPath.endsWith('Material')) {
                    // Don't update material field
                    setFieldValue(fieldPath, value);
                }
            });

            logDebug('useFormCascade', 'Base geogrid updated with LCR recalculation', undefined, LogTag.FORM_CASCADE);
        } catch (error) {
            logError('useFormCascade', 'Error in base geogrid change:', error, LogTag.FORM_CASCADE);
        }
    }, []);

    /**
     * Handle subbase geogrid change
     * Refactored to use MaterialCascadeService
     */
    const handleSubbaseGeogridChange = useCallback((geogridId: string, setFieldValue: (field: string, value: any) => void, values: T) => {
        logDebug('useFormCascade', `Subbase geogrid changed to: ${geogridId}`, undefined, LogTag.FORM_CASCADE);

        // Early return if required parameters not provided
        if (!setFieldValue || !values) {
            logDebug('useFormCascade', 'setFieldValue or values not provided, skipping subbase geogrid change', undefined, LogTag.FORM_CASCADE);
            return;
        }

        if (!values.subbaseMaterial) {
            logDebug('useFormCascade', 'No subbase material selected, cannot update geogrid', undefined, LogTag.FORM_CASCADE);
            return;
        }

        try {
            // Validate geogrid compatibility using service
            const isValid = materialCascadeService.validateGeogridCompatibility('subbase', values.subbaseMaterial, geogridId);
            if (!isValid && geogridId) {
                logDebug('useFormCascade', `Invalid subbase geogrid selection for material: ${values.subbaseMaterial}`, undefined, LogTag.FORM_CASCADE);
                return;
            }

            // Update subbase geogrid field
            setFieldValue('subbaseCourse.subbaseGeogrid', geogridId);

            // Recalculate cascade effects with new geogrid using service
            const fieldUpdates = materialCascadeService.calculateMaterialCascadeUpdate('subbase', values.subbaseMaterial, geogridId);

            // Apply relevant field updates (excluding material ID since it's not changing)
            Object.entries(fieldUpdates).forEach(([fieldPath, value]) => {
                if (!fieldPath.endsWith('Material')) {
                    // Don't update material field
                    setFieldValue(fieldPath, value);
                }
            });

            logDebug('useFormCascade', 'Subbase geogrid updated with LCR recalculation', undefined, LogTag.FORM_CASCADE);
        } catch (error) {
            logError('useFormCascade', 'Error in subbase geogrid change:', error, LogTag.FORM_CASCADE);
        }
    }, []);

    /**
     * Initialize form with proper material defaults
     * Call this when form is first loaded to ensure all fields are consistent
     * Refactored to use MaterialCascadeService for consistency
     */
    /**
     * Handle railway geogrid change and update tensile strength
     * Railway Logic: selectedGeogrid → tensileStrengthAt2Percent calculation
     */
    const handleRailwayGeogridChange = useCallback((selectedGeogrid: string, setFieldValue: (field: string, value: any) => void) => {
        logDebug('useFormCascade', `Railway geogrid changed to: ${selectedGeogrid}`, undefined, LogTag.FORM_CASCADE);

        // Early return if required parameters not provided
        if (!setFieldValue) {
            logDebug('useFormCascade', 'setFieldValue not provided, skipping railway geogrid change', undefined, LogTag.FORM_CASCADE);
            return;
        }

        try {
            // Calculate the cascade effects
            const effects = calculateRailwayGeogridCascadeEffects(selectedGeogrid);

            logDebug('useFormCascade', 'Railway geogrid cascade effects:', effects, LogTag.FORM_CASCADE);

            // Update the tensile strength field
            setFieldValue('geoGrid.tensileStrengthAt2Percent', effects.tensileStrengthAt2Percent);

            logDebug('useFormCascade', `Updated tensile strength to: ${effects.tensileStrengthAt2Percent}`, undefined, LogTag.FORM_CASCADE);
        } catch (error) {
            logError('useFormCascade', 'Error in railway geogrid change:', error, LogTag.FORM_CASCADE);
        }
    }, []);

    /** A type selection resets CBR; saved projects never enter this event handler on load. */
    const handleBallastTypeChange = useCallback((ballastType: string, setFieldValue: (field: string, value: unknown) => void, values?: T) => {
        const materials = values?.materialTypes as { ballastType?: unknown } | undefined;
        if (!setFieldValue || ballastType === materials?.ballastType || !getBallastOptions().some(option => option.value === ballastType)) return;
        try {
            setFieldValue('soilProperties.cbrOfBallast', getBallastCbr(ballastType) ?? '');
        } catch (error) {
            logError('useFormCascade', 'Error in ballast type cascade', error);
        }
    }, []);

const initializeFormMaterials = useCallback((setFieldValue: (field: string, value: any) => void, values: T) => {
        logDebug('useFormCascade', 'Initializing form materials', undefined, LogTag.FORM_CASCADE);

        // Early return if values not properly initialized
        if (!setFieldValue || !values) {
            logDebug('useFormCascade', 'setFieldValue or values not provided, skipping material initialization', undefined, LogTag.FORM_CASCADE);
            return;
        }

        try {
            // Initialize base material cascade if base material is selected
            // Access NESTED structure: values.baseCourse.baseMaterial (not flat values.baseMaterial)
            const baseMaterial = (values as any)?.baseCourse?.baseMaterial;
            const baseGeogrid = (values as any)?.baseCourse?.baseGeogrid;
            if (baseMaterial) {
                const fieldUpdates = materialCascadeService.calculateMaterialCascadeUpdate('base', baseMaterial, baseGeogrid || null);

                // Apply field updates (excluding material, geogrid, and minThickness fields)
                // 🔧 FIX: Exclude minThickness to preserve converted Imperial values
                Object.entries(fieldUpdates).forEach(([fieldPath, value]) => {
                    if (!fieldPath.endsWith('Material') && !fieldPath.endsWith('Geogrid') && !fieldPath.includes('minThickness')) {
                        setFieldValue(fieldPath, value);
                    }
                });
            }

            // Initialize subbase material cascade if subbase material is selected
            // Access NESTED structure: values.subbaseCourse.subbaseMaterial
            const subbaseMaterial = (values as any)?.subbaseCourse?.subbaseMaterial;
            const subbaseGeogrid = (values as any)?.subbaseCourse?.subbaseGeogrid;
            if (subbaseMaterial) {
                const fieldUpdates = materialCascadeService.calculateMaterialCascadeUpdate('subbase', subbaseMaterial, subbaseGeogrid || null);

                // Apply field updates (excluding material, geogrid, and minThickness fields)
                // 🔧 FIX: Exclude minThickness to preserve converted Imperial values
                Object.entries(fieldUpdates).forEach(([fieldPath, value]) => {
                    if (!fieldPath.endsWith('Material') && !fieldPath.endsWith('Geogrid') && !fieldPath.includes('minThickness')) {
                        setFieldValue(fieldPath, value);
                    }
                });
            }

            // Initialize surface material cascade if surface material is selected
            // Access NESTED structure: values.surface.surfaceMaterial
            const surfaceMaterial = (values as any)?.surface?.surfaceMaterial;
            if (surfaceMaterial) {
                const fieldUpdates = materialCascadeService.calculateMaterialCascadeUpdate('surface', surfaceMaterial);

                // Apply field updates (excluding material, geogrid, and minThickness fields)
                // 🔧 FIX: Exclude minThickness fields to preserve converted Imperial values
                // The cascade returns METRIC values, but form initialValues may already be converted to Imperial.
                // minThickness should come from initialValues (already unit-converted), not from cascade defaults.
                Object.entries(fieldUpdates).forEach(([fieldPath, value]) => {
                    if (!fieldPath.endsWith('Material') && !fieldPath.endsWith('Geogrid') && !fieldPath.includes('minThickness')) {
                        setFieldValue(fieldPath, value);
                    }
                });
            }

            logDebug('useFormCascade', 'Form materials initialization completed', undefined, LogTag.FORM_CASCADE);
        } catch (error) {
            logError('useFormCascade', 'Error in form materials initialization:', error, LogTag.FORM_CASCADE);
        }
    }, []);

    /**
     * Centralized field change handler that routes to appropriate cascade handlers
     * This provides a single entry point for all material and geogrid cascade logic
     */
    const handleFieldChange = useCallback(
        (fieldName: string, value: string, setFieldValue: (field: string, value: any) => void, values?: T) => {
            logDebug('useFormCascade', `🎯 Centralized cascade router: ${fieldName} = ${value}`, undefined, LogTag.FORM_CASCADE);

            switch (fieldName) {
                case 'surfaceMaterial':
                    if (values) {
                        handleSurfaceMaterialChange(value, setFieldValue, values);
                    }
                    break;
                case 'baseMaterial':
                    if (values) {
                        handleBaseMaterialChange(value, setFieldValue, values);
                    }
                    break;
                case 'subbaseMaterial':
                    if (values) {
                        handleSubbaseMaterialChange(value, setFieldValue, values);
                    }
                    break;
                case 'baseGeogrid':
                    if (values) {
                        handleBaseGeogridChange(value, setFieldValue, values);
                    }
                    break;
                case 'subbaseGeogrid':
                    if (values) {
                        handleSubbaseGeogridChange(value, setFieldValue, values);
                    }
                    break;
                case 'selectedGeogrid':
                    handleRailwayGeogridChange(value, setFieldValue);
                    break;
                case 'ballastType':
                    handleBallastTypeChange(value, setFieldValue, values);
                    break;
                default:
                    logDebug('useFormCascade', `No cascade handler for field: ${fieldName}`, undefined, LogTag.FORM_CASCADE);
                    break;
            }
        },
        [handleSurfaceMaterialChange, handleBaseMaterialChange, handleSubbaseMaterialChange, handleBaseGeogridChange, handleSubbaseGeogridChange, handleRailwayGeogridChange, handleBallastTypeChange]
    );

    /**
     * Helper function to check if a field name should trigger cascade logic
     */
    const isFieldCascadable = useCallback((fieldName: string): boolean => {
        const cascadableFields = ['surfaceMaterial', 'baseMaterial', 'subbaseMaterial', 'baseGeogrid', 'subbaseGeogrid', 'selectedGeogrid', 'ballastType'];
        return cascadableFields.includes(fieldName);
    }, []);

    return {
        // Individual handlers (for backward compatibility)
        handleBaseMaterialChange,
        handleSubbaseMaterialChange,
        handleSurfaceMaterialChange,
        handleBaseGeogridChange,
        handleSubbaseGeogridChange,
        handleRailwayGeogridChange,
        handleBallastTypeChange,
        initializeFormMaterials,
        // New centralized handlers
        handleFieldChange,
        isFieldCascadable,
    };
};

// Backward compatibility export
export const useFormMaterialCascade = useFormCascade;

// New default export
export default useFormCascade;
